import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Plus, Trash2, Edit2, FileText, 
  Upload, Calendar, Hash, Save,
  Database, File, Image, Music,
  Video, FileSpreadsheet,
  AlertCircle
} from 'lucide-react';

const ModalPortal = ({ children }) => {
  return createPortal(children, document.body);
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const KnowledgeBaseManager = ({ knowledges = [], onChange, onSave }) => {
  const [knowledgeList, setKnowledgeList] = useState(knowledges);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Estado para nova base de conhecimento
  const [newKnowledge, setNewKnowledge] = useState({
    name: '',
    description: '',
    contents: []
  });
  
  // Estado para novo conteúdo
  const [newContent, setNewContent] = useState({
    type: 'text',
    content: '',
    created_at: new Date().toISOString()
  });
  
  const [showContentForm, setShowContentForm] = useState(false);
  
  // Estados para upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Configurações de upload
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const SUPPORTED_FILE_TYPES = {
    'application/pdf': { icon: FileText, color: 'text-red-600', name: 'PDF' },
    'application/msword': { icon: FileText, color: 'text-blue-600', name: 'Word' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, color: 'text-blue-600', name: 'Word' },
    'application/vnd.ms-excel': { icon: FileSpreadsheet, color: 'text-green-600', name: 'Excel' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileSpreadsheet, color: 'text-green-600', name: 'Excel' },
    'text/csv': { icon: FileSpreadsheet, color: 'text-green-600', name: 'CSV' },
    'text/plain': { icon: FileText, color: 'text-gray-600', name: 'TXT' },
    'image/jpeg': { icon: Image, color: 'text-purple-600', name: 'JPEG' },
    'image/jpg': { icon: Image, color: 'text-purple-600', name: 'JPG' },
    'image/png': { icon: Image, color: 'text-purple-600', name: 'PNG' },
    'image/gif': { icon: Image, color: 'text-purple-600', name: 'GIF' },
    'audio/mpeg': { icon: Music, color: 'text-pink-600', name: 'MP3' },
    'audio/mp3': { icon: Music, color: 'text-pink-600', name: 'MP3' },
    'audio/wav': { icon: Music, color: 'text-pink-600', name: 'WAV' },
    'audio/ogg': { icon: Music, color: 'text-pink-600', name: 'OGG' },
    'video/mp4': { icon: Video, color: 'text-indigo-600', name: 'MP4' }
  };

  // Validar arquivo
  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'Nenhum arquivo selecionado' };
    
    const fileType = file.type || '';
    const isSupported = Object.keys(SUPPORTED_FILE_TYPES).includes(fileType) || 
                       (file.name && Object.keys(SUPPORTED_FILE_TYPES).some(type => 
                         file.name.toLowerCase().endsWith('.' + SUPPORTED_FILE_TYPES[type].name.toLowerCase())
                       ));
    
    if (!isSupported) {
      return { valid: false, error: 'Tipo de arquivo não suportado' };
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `Arquivo muito grande (máx: ${MAX_FILE_SIZE / 1024 / 1024}MB)` };
    }
    
    return { valid: true };
  };
  
  // Obter ícone do arquivo
  const getFileIcon = (file) => {
    if (!file) return { Icon: File, color: 'text-gray-500' };
    
    const fileType = file.type || '';
    const fileInfo = SUPPORTED_FILE_TYPES[fileType];
    
    if (fileInfo) return { Icon: fileInfo.icon, color: fileInfo.color };
    
    // Tentar por extensão
    const extension = file.name?.split('.').pop()?.toLowerCase();
    for (const [type, info] of Object.entries(SUPPORTED_FILE_TYPES)) {
      if (info.name.toLowerCase() === extension) {
        return { Icon: info.icon, color: info.color };
      }
    }
    
    return { Icon: File, color: 'text-gray-500' };
  };
  
  // Upload de arquivo
  const handleFileUpload = async () => {
    if (!selectedFile) {
      setUploadError('Selecione um arquivo primeiro');
      return;
    }
    
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
        const token = localStorage.getItem('authToken');

      const response = await fetch(API_BASE_URL + '/files/upload-and-process', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Erro no upload: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      const fileContent = {
        type: 'file',
        content: result.data.processed_content.content,
        url: result.data.file_url,
        metadata: {
          original_filename: selectedFile.name,
          file_type: selectedFile.type,
          file_size: selectedFile.size,
          processing_result: result
        },
        created_at: new Date().toISOString()
      };
      
      if (editingKnowledge) {
        const updatedKnowledge = {
          ...editingKnowledge,
          contents: [...(editingKnowledge.contents || []), fileContent]
        };
        
        const updatedList = [...knowledgeList];
        updatedList[editingIndex] = updatedKnowledge;
        setKnowledgeList(updatedList);
        setEditingKnowledge(updatedKnowledge);
        onChange(updatedList);
      } else {
        setNewKnowledge({
          ...newKnowledge,
          contents: [...(newKnowledge.contents || []), fileContent]
        });
      }
      
      // Limpar e fechar
      setSelectedFile(null);
      setUploadProgress(100);
      setTimeout(() => {
        setShowContentForm(false);
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
      
    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadError(error.message || 'Erro ao processar arquivo');
      setIsUploading(false);
    }
  };
  
  // Handlers de drag & drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      const validation = validateFile(file);
      if (validation.valid) {
        setSelectedFile(file);
        setUploadError(null);
      } else {
        setUploadError(validation.error);
      }
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateFile(file);
      if (validation.valid) {
        setSelectedFile(file);
        setUploadError(null);
      } else {
        setUploadError(validation.error);
      }
    }
  };

  // Adicionar nova base de conhecimento
  const handleAddKnowledge = () => {
    if (!newKnowledge.name.trim()) {
      alert('Nome da base é obrigatório');
      return;
    }
    
    const knowledge = {
      ...newKnowledge,
      contents: newKnowledge.contents || [],
      created_at: new Date().toISOString()
    };
    
    const updatedList = [...knowledgeList, knowledge];
    setKnowledgeList(updatedList);
    onChange(updatedList);
    
    // Reset
    setNewKnowledge({ name: '', description: '', contents: [] });
    setShowAddModal(false);
  };
  
  // Adicionar conteúdo de texto
  const handleAddTextContent = () => {
    if (!newContent.content.trim()) {
      alert('Conteúdo não pode estar vazio');
      return;
    }
    
    const content = {
      ...newContent,
      created_at: new Date().toISOString()
    };
    
    if (editingKnowledge) {
      const updatedKnowledge = {
        ...editingKnowledge,
        contents: [...(editingKnowledge.contents || []), content]
      };
      
      const updatedList = [...knowledgeList];
      updatedList[editingIndex] = updatedKnowledge;
      setKnowledgeList(updatedList);
      setEditingKnowledge(updatedKnowledge);
      onChange(updatedList);
    } else {
      setNewKnowledge({
        ...newKnowledge,
        contents: [...(newKnowledge.contents || []), content]
      });
    }
    
    // Reset content form
    setNewContent({ type: 'text', content: '', created_at: new Date().toISOString() });
    setShowContentForm(false);
  };
  
  // Remover base de conhecimento
  const handleRemoveKnowledge = (index) => {
    if (window.confirm('Tem certeza que deseja remover esta base de conhecimento?')) {
      const updatedList = knowledgeList.filter((_, i) => i !== index);
      setKnowledgeList(updatedList);
      onChange(updatedList);
    }
  };
  
  // Remover conteúdo
  const handleRemoveContent = (contentIndex) => {
    if (editingKnowledge) {
      const updatedContents = editingKnowledge.contents.filter((_, i) => i !== contentIndex);
      const updatedKnowledge = { ...editingKnowledge, contents: updatedContents };
      setEditingKnowledge(updatedKnowledge);
      
      const updatedList = [...knowledgeList];
      updatedList[editingIndex] = updatedKnowledge;
      setKnowledgeList(updatedList);
      onChange(updatedList);
    } else {
      const updatedContents = newKnowledge.contents.filter((_, i) => i !== contentIndex);
      setNewKnowledge({ ...newKnowledge, contents: updatedContents });
    }
  };
  
  // Abrir modal de edição
  const handleEditKnowledge = (knowledge, index) => {
    setEditingKnowledge({ ...knowledge });
    setEditingIndex(index);
    setShowEditModal(true);
  };
  
  // Salvar edição
  const handleSaveEdit = () => {
    if (!editingKnowledge.name.trim()) {
      alert('Nome da base é obrigatório');
      return;
    }
    
    const updatedList = [...knowledgeList];
    updatedList[editingIndex] = editingKnowledge;
    setKnowledgeList(updatedList);
    onChange(updatedList);
    
    setEditingKnowledge(null);
    setEditingIndex(null);
    setShowEditModal(false);
  };
  
  // Salvar todas as bases
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await onSave(knowledgeList);
    } catch (error) {
      console.error('Erro ao salvar:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Formatar data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Formatar tamanho de arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  // Truncar texto para preview
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Handlers para o modal de conteúdo
  const handleContentTypeChange = (type) => {
    setNewContent({ ...newContent, type });
    setSelectedFile(null);
    setUploadError(null);
  };
  
  const handleContentTextChange = (e) => {
    setNewContent({ ...newContent, content: e.target.value });
  };
  
  const handleCloseContentForm = () => {
    setShowContentForm(false);
    setNewContent({ type: 'text', content: '', created_at: new Date().toISOString() });
    setSelectedFile(null);
    setUploadError(null);
    setIsUploading(false);
  };
  
  // Handlers para o modal de conhecimento
  const handleKnowledgeNameChange = (e, isEditing) => {
    const value = e.target.value;
    if (isEditing) {
      setEditingKnowledge({ ...editingKnowledge, name: value });
    } else {
      setNewKnowledge({ ...newKnowledge, name: value });
    }
  };
  
  const handleKnowledgeDescriptionChange = (e, isEditing) => {
    const value = e.target.value;
    if (isEditing) {
      setEditingKnowledge({ ...editingKnowledge, description: value });
    } else {
      setNewKnowledge({ ...newKnowledge, description: value });
    }
  };
  
  const handleCloseKnowledgeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingKnowledge(null);
    setEditingIndex(null);
    setNewKnowledge({ name: '', description: '', contents: [] });
  };
  
  // Renderizar ícone de conteúdo
  const renderContentIcon = (content) => {
    if (content.type === 'text') {
      return <FileText className="w-4 h-4 text-gray-600" />;
    }
    
    if (content.metadata?.file_type) {
      const fileInfo = SUPPORTED_FILE_TYPES[content.metadata.file_type];
      if (fileInfo) {
        const IconComponent = fileInfo.icon;
        return <IconComponent className={`w-4 h-4 ${fileInfo.color}`} />;
      }
    }
    
    return <File className="w-4 h-4 text-gray-600" />;
  };
  
  return (
    <>
      <div className="space-y-6">
        {/* Lista de bases de conhecimento */}
        <div className="space-y-4">
          {knowledgeList.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
              <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-700 mb-2">
                Nenhuma base de conhecimento configurada
              </h4>
              <p className="text-gray-500 mb-6">
                Adicione bases de conhecimento para melhorar as respostas do seu funcionário
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Criar Primeira Base
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4">
                {knowledgeList.map((knowledge, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Database className="w-5 h-5 text-indigo-600" />
                          <h4 className="text-lg font-semibold text-gray-900">{knowledge.name}</h4>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                            {knowledge.contents?.length || 0} conteúdos
                          </span>
                        </div>
                        {knowledge.description && (
                          <p className="text-gray-600 mb-3">{knowledge.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(knowledge.created_at || new Date().toISOString())}
                          </span>
                          <span className="flex items-center gap-1">
                            <Hash className="w-4 h-4" />
                            {knowledge.contents?.reduce((acc, c) => acc + (c.content?.length || 0), 0)} caracteres
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEditKnowledge(knowledge, index)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleRemoveKnowledge(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remover"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setShowAddModal(true)}
                className="w-full py-3 border-2 border-dashed border-indigo-300 rounded-xl text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2 font-semibold"
              >
                <Plus className="w-5 h-5" />
                Adicionar Nova Base de Conhecimento
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de Adicionar Conteúdo */}
      {showContentForm && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-fade-in">
              <div className="p-6 border-b">
                <h3 className="text-xl font-bold text-gray-900">Adicionar Conteúdo</h3>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                {/* Tipo de conteúdo */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Conteúdo
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleContentTypeChange('text')}
                      className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                        newContent.type === 'text'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <FileText className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Texto</span>
                    </button>
                    <button
                      onClick={() => handleContentTypeChange('file')}
                      className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                        newContent.type === 'file'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Upload className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">Arquivo</span>
                    </button>
                  </div>
                </div>
                
                {/* Campo de conteúdo de texto */}
                {newContent.type === 'text' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Conteúdo de Texto
                    </label>
                    <textarea
                      value={newContent.content}
                      onChange={handleContentTextChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all resize-none"
                      rows={6}
                      maxLength={20000}
                      placeholder="Cole ou digite o conteúdo aqui..."
                    />
                    <div className="flex justify-between mt-2">
                      <span className="text-sm text-gray-500">
                        Máximo 20.000 caracteres
                      </span>
                      <span className="text-sm text-gray-500">
                        {newContent.content.length}/20000
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Upload de arquivo */}
                {newContent.type === 'file' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Upload de Arquivo
                    </label>
                    
                    {/* Área de drag & drop */}
                    <div
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        isDragging
                          ? 'border-indigo-500 bg-indigo-50'
                          : selectedFile
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.mp3,.wav,.ogg,.mp4"
                        className="hidden"
                      />
                      
                      {!selectedFile ? (
                        <>
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-base font-medium text-gray-700 mb-1">
                            {isDragging ? 'Solte o arquivo aqui' : 'Clique ou arraste um arquivo'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Tipos suportados: PDF, Word, Excel, CSV, TXT, Imagens, Áudio, Vídeo
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            Tamanho máximo: 20MB
                          </p>
                        </>
                      ) : (
                        <div className="space-y-3">
                          {(() => {
                            const { Icon, color } = getFileIcon(selectedFile);
                            return (
                              <>
                                <Icon className={`w-12 h-12 mx-auto ${color}`} />
                                <div>
                                  <p className="text-base font-medium text-gray-900 mb-1">
                                    {selectedFile.name}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {formatFileSize(selectedFile.size)}
                                  </p>
                                </div>
                                {!isUploading && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedFile(null);
                                    }}
                                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                                  >
                                    Remover arquivo
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Progresso de upload */}
                      {isUploading && (
                        <div className="absolute inset-0 bg-white/90 rounded-xl flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3"></div>
                            <p className="text-sm font-medium text-gray-700">Processando arquivo...</p>
                            <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Mensagem de erro */}
                    {uploadError && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Erro no upload</p>
                          <p className="text-sm text-red-600 mt-1">{uploadError}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Lista de tipos suportados */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-2">Tipos de arquivo suportados:</p>
                      <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                        <div>• PDF, Word (.doc, .docx)</div>
                        <div>• Excel (.xls, .xlsx), CSV</div>
                        <div>• Texto (.txt)</div>
                        <div>• Imagens (JPG, PNG, GIF)</div>
                        <div>• Áudio (MP3, WAV, OGG)</div>
                        <div>• Vídeo (MP4)</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={handleCloseContentForm}
                  className="px-5 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={newContent.type === 'text' ? handleAddTextContent : handleFileUpload}
                  disabled={
                    (newContent.type === 'text' && !newContent.content.trim()) ||
                    (newContent.type === 'file' && !selectedFile) ||
                    isUploading
                  }
                  className="px-5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Adicionar Conteúdo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Modal de Adicionar Base */}
      {showAddModal && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6">
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <Database className="w-7 h-7" />
                  Nova Base de Conhecimento
                </h2>
              </div>
              
              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Nome e Descrição */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome da Base <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newKnowledge.name}
                      onChange={(e) => handleKnowledgeNameChange(e, false)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all"
                      placeholder="Ex: FAQ de Vendas, Políticas da Empresa..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Descrição
                    </label>
                    <textarea
                      value={newKnowledge.description}
                      onChange={(e) => handleKnowledgeDescriptionChange(e, false)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all resize-none"
                      rows={2}
                      placeholder="Descreva o propósito desta base de conhecimento..."
                    />
                  </div>
                </div>
                
                {/* Conteúdos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      Conteúdos ({newKnowledge.contents?.length || 0})
                    </h3>
                    <button
                      onClick={() => setShowContentForm(true)}
                      className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition-all flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar Conteúdo
                    </button>
                  </div>
                  
                  {/* Lista de conteúdos */}
                  <div className="space-y-3">
                    {newKnowledge.contents?.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium">Nenhum conteúdo adicionado</p>
                        <p className="text-sm text-gray-500 mt-1">Clique no botão acima para adicionar</p>
                      </div>
                    ) : (
                      newKnowledge.contents?.map((content, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {renderContentIcon(content)}
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                                  {content.type === 'text' ? 'Texto' : 'Arquivo'}
                                </span>
                                {content.metadata?.original_filename && (
                                  <span className="text-xs text-gray-600 font-medium">
                                    {content.metadata.original_filename}
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">
                                  {formatDate(content.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">
                                {truncateText(content.content, 150)}
                              </p>
                              {content.metadata?.file_size && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Tamanho: {formatFileSize(content.metadata.file_size)}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveContent(idx)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-3"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-6 border-t flex justify-between">
                <button
                  onClick={handleCloseKnowledgeModal}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddKnowledge}
                  disabled={!newKnowledge.name.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  Criar Base
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Modal de Editar Base */}
      {showEditModal && editingKnowledge && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
              {/* Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6">
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <Database className="w-7 h-7" />
                  Editar Base de Conhecimento
                </h2>
              </div>
              
              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Nome e Descrição */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome da Base <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingKnowledge.name}
                      onChange={(e) => handleKnowledgeNameChange(e, true)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all"
                      placeholder="Ex: FAQ de Vendas, Políticas da Empresa..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Descrição
                    </label>
                    <textarea
                      value={editingKnowledge.description}
                      onChange={(e) => handleKnowledgeDescriptionChange(e, true)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 transition-all resize-none"
                      rows={2}
                      placeholder="Descreva o propósito desta base de conhecimento..."
                    />
                  </div>
                </div>
                
                {/* Conteúdos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      Conteúdos ({editingKnowledge.contents?.length || 0})
                    </h3>
                    <button
                      onClick={() => setShowContentForm(true)}
                      className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition-all flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar Conteúdo
                    </button>
                  </div>
                  
                  {/* Lista de conteúdos */}
                  <div className="space-y-3">
                    {editingKnowledge.contents?.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium">Nenhum conteúdo adicionado</p>
                        <p className="text-sm text-gray-500 mt-1">Clique no botão acima para adicionar</p>
                      </div>
                    ) : (
                      editingKnowledge.contents?.map((content, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {renderContentIcon(content)}
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                                  {content.type === 'text' ? 'Texto' : 'Arquivo'}
                                </span>
                                {content.metadata?.original_filename && (
                                  <span className="text-xs text-gray-600 font-medium">
                                    {content.metadata.original_filename}
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">
                                  {formatDate(content.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">
                                {truncateText(content.content, 150)}
                              </p>
                              {content.metadata?.file_size && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Tamanho: {formatFileSize(content.metadata.file_size)}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveContent(idx)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-3"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-6 border-t flex justify-between">
                <button
                  onClick={handleCloseKnowledgeModal}
                  className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editingKnowledge.name.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Estilos para animações */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default KnowledgeBaseManager;